// L'ADRESSE d'une entrée du journal d'actions.
//
// LE PROBLÈME (demande user 2026-10-06) : une ligne du journal disait qui, quand et quelle action, mais pas SUR
// QUELLE ADRESSE. Le JVID n'apparaissait que dans le chemin de la requête (« …/addresses/12770041/columns/… »),
// la rue et le numéro seulement dans les 105 clés de l'image de ligne, une fois l'entrée dépliée.
//
// Rien n'est stocké en plus : comme le résumé (`summariseMutations`), l'adresse est RECONSTITUÉE À LA LECTURE
// depuis ce que l'entrée porte déjà — tout l'historique en bénéficie, sans migration ni rattrapage.
//
// Trois sources, de la plus fidèle à la moins fidèle :
//  • `image`       — la ligne de `public.address` photographiée par l'écriture : l'adresse AU MOMENT de l'action ;
//  • `ticket`      — la fiche du ticket (`addrDmpSnapshot`), quand l'action n'a touché que le ticket ou la file ;
//  • `declaration` — la ligne de la mapping table : l'adresse telle que l'OPÉRATEUR la déclare, qui peut différer
//                    de la nôtre (c'est la raison d'être de cette table). On la montre faute de mieux, et on le dit.
// Aucune relecture du DMP : le journal doit rester lisible quand le DMP ne répond pas — c'est l'outil avec
// lequel on enquête sur ses écritures.
const isRow = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const text = (v) => {
    if (typeof v === 'number')
        return String(v);
    if (typeof v !== 'string')
        return null;
    const t = v.trim();
    return t === '' ? null : t;
};
/** Un JVID est un entier positif ; les colonnes int8 arrivent en chaîne (voir `jsonSafe`). */
const asJvid = (v) => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
};
/** Lit une adresse dans une ligne, qu'elle vienne du DMP (snake_case) ou de la fiche d'un ticket (camelCase).
 *  null quand la ligne ne porte ni rue ni numéro : un JVID seul n'est pas une adresse. */
export function logAddressFromRow(row, source, jvid = null) {
    if (!isRow(row))
        return null;
    const pick = (snake, camel) => text(row[snake]) ?? text(row[camel]);
    const streetName = pick('street_name', 'streetName');
    const houseNumber = pick('house_number', 'houseNumber');
    const houseNumberExtension = pick('house_number_extension', 'houseNumberExtension');
    if (streetName == null && houseNumber == null && houseNumberExtension == null)
        return null;
    return {
        jvid: asJvid(row.jvid) ?? jvid,
        streetName,
        houseNumber,
        houseNumberExtension,
        busNumber: pick('bus_number', 'busNumber'),
        postalCode: pick('postal_code', 'postalCode'),
        // La ville d'abord : c'est elle que les relecteurs lisent ; la commune ne sert que de repli.
        city: text(row.city) ?? text(row.municipality),
        dwellingUnitType: pick('dwelling_unit_type', 'dwellingUnitType'),
        source,
    };
}
const jvidOnly = (jvid) => ({
    jvid, streetName: null, houseNumber: null, houseNumberExtension: null, busNumber: null,
    postalCode: null, city: null, dwellingUnitType: null, source: null,
});
/** Les JVID qu'UNE mutation désigne : la clé d'une écriture brute, le `where` de Prisma, puis les images. */
function mutationJvids(m) {
    const out = [];
    const push = (v) => { const j = asJvid(v); if (j != null && !out.includes(j))
        out.push(j); };
    if (isRow(m.key)) {
        push(m.key.jvid);
        if (isRow(m.key.jvid_operatorId))
            push(m.key.jvid_operatorId.jvid);
    }
    for (const image of [m.after, m.before]) {
        if (!isRow(image))
            continue;
        push(image.jvid);
        // La trace d'audit range le JVID dans `details`, ou dans `entityId` quand l'entité EST l'adresse.
        if (m.table === 'TtAuditLog') {
            if (isRow(image.details))
                push(image.details.jvid);
            if (image.entityType === 'address')
                push(image.entityId);
        }
    }
    return out;
}
const PATH_JVID = /\/addresses\/(\d+)(?:\/|$)/;
/** Le JVID que le chemin de la requête vise — « …/addresses/12770041/columns/… ». */
export function jvidFromLogPath(path) {
    const m = PATH_JVID.exec(path ?? '');
    return m ? asJvid(m[1]) : null;
}
/**
 * Les adresses d'une action, depuis ses mutations et son chemin.
 *
 * L'adresse PRINCIPALE est celle que le chemin vise quand l'action l'a touchée ; sinon la première touchée.
 * Le chemin seul ne suffit pas : « ajouter des boîtes » vise une unité existante et en crée d'autres — ce sont
 * les créées qui comptent, l'unité visée n'a pas bougé.
 */
export function summariseLogAddresses(mutations, path) {
    const list = Array.isArray(mutations) ? mutations : [];
    const order = [];
    const best = new Map();
    // Une image vaut mieux que la fiche d'un ticket, qui vaut mieux qu'une déclaration.
    const rank = (s) => (s === 'image' ? 3 : s === 'ticket' ? 2 : s === 'declaration' ? 1 : 0);
    const keep = (jvid, a) => {
        if (!order.includes(jvid))
            order.push(jvid);
        const candidate = a ?? jvidOnly(jvid);
        const current = best.get(jvid);
        if (!current || rank(candidate.source) > rank(current.source))
            best.set(jvid, { ...candidate, jvid });
    };
    for (const m of list) {
        const table = m.table ?? '';
        // L'état APRÈS l'écriture : une correction de numéro doit afficher le numéro corrigé. `before` ne sert
        // qu'à une suppression.
        const image = isRow(m.after) ? m.after : isRow(m.before) ? m.before : null;
        const jvids = mutationJvids(m);
        let address = null;
        if (image && table === 'public.address')
            address = logAddressFromRow(image, 'image', jvids[0] ?? null);
        else if (image && table === 'public.address_mapping_table')
            address = logAddressFromRow(image, 'declaration', jvids[0] ?? null);
        else if (image && table === 'TtTicket')
            address = logAddressFromRow(image.addrDmpSnapshot, 'ticket', asJvid(image.jvid));
        if (address?.jvid != null)
            keep(address.jvid, address);
        for (const j of jvids)
            keep(j, null);
    }
    const target = jvidFromLogPath(path);
    const known = (j) => (j != null && best.get(j)?.source != null ? j : null);
    // Visée ET documentée → elle. Sinon la première adresse documentée ; sinon la visée, puis la première touchée.
    const primaryJvid = known(target)
        ?? order.find((j) => best.get(j)?.source != null)
        ?? (target != null && order.includes(target) ? target : null)
        ?? order[0]
        ?? target;
    if (primaryJvid == null)
        return { primary: null, addresses: [] };
    const primary = best.get(primaryJvid) ?? jvidOnly(primaryJvid);
    const addresses = [primary, ...order.filter((j) => j !== primaryJvid).map((j) => best.get(j) ?? jvidOnly(j))];
    return { primary, addresses };
}
/** « Rue de Namur 127A ». Dans le DMP comme dans la mapping table, `house_number_extension` porte le numéro
 *  COMPLET (« 127 » → « 127A ») : quand il prolonge le numéro par un suffixe, on n'affiche que lui — sinon
 *  « 127 127A ». Un couple incohérent (n° 5, extension 58A) reste affiché en entier : le masquer cacherait
 *  précisément ce qu'on vient chercher dans un journal. */
export function logAddressLine(a) {
    const number = a.houseNumber ?? '';
    const ext = a.houseNumberExtension ?? '';
    const suffix = number !== '' && ext.startsWith(number) ? ext.slice(number.length) : null;
    const full = ext === '' || ext === number ? number
        : suffix != null && !/^\d/.test(suffix) ? ext
            : `${number} ${ext}`.trim();
    return [a.streetName, full].filter(Boolean).join(' ');
}
/** « 5000 Namur ». */
export function logAddressLocality(a) {
    return [a.postalCode, a.city].filter(Boolean).join(' ');
}
/** L'adresse sur une ligne, prête à être copiée : « Rue de Namur 127A bte 45, 5000 Namur ». */
export function logAddressLabel(a) {
    const line = logAddressLine(a) + (a.busNumber ? ` bte ${a.busNumber}` : '');
    return [line, logAddressLocality(a)].filter((s) => s.trim() !== '').join(', ');
}
//# sourceMappingURL=action-address.js.map